// Módulo 4: lo que el sistema mandó (o habría mandado) por correo, en un solo lugar.
import { Boton, EsqueletoTabla, Pill, TituloPagina, Vacio } from "@iavmx/ui";
import { useAccion, useAvisos, useConSesion, useEsMercadotecnia } from "./consultas";
import { Nota } from "./componentes";
import { fechaHora } from "./util";

function plural(n: number, uno: string, varios: string) {
  return `${n} ${n === 1 ? uno : varios}`;
}

function tonoDe(titulo: string): "rojo" | "ambar" | "azul" | "neutro" | "verde" {
  if (/^(en riesgo|escalada|vencida|retraso)/i.test(titulo)) return "rojo";
  if (/^(vence|falta información|pide mover|aprobación pendiente)/i.test(titulo)) return "ambar";
  if (/^(entregado|aprobado)/i.test(titulo)) return "verde";
  if (/^(tarea nueva|rechazado)/i.test(titulo)) return "azul";
  return "neutro";
}

function categoria(titulo: string): string {
  if (/^(en riesgo|escalada)/i.test(titulo)) return "Riesgo";
  if (/^(vencida|retraso)/i.test(titulo)) return "Retraso";
  if (/^vence/i.test(titulo)) return "Recordatorio";
  if (/^(pide mover|aprobación pendiente|aprobado|rechazado)/i.test(titulo)) return "Aprobación";
  if (/^falta información/i.test(titulo)) return "Incompleta";
  if (/^(entregado|tarea nueva)/i.test(titulo)) return "Entrega";
  return "Aviso";
}

export function Avisos() {
  const conSesion = useConSesion();
  const esMkt = useEsMercadotecnia();
  const q = useAvisos();
  const leida = useAccion<string>({
    fn: (api, id) => api.api.campanas.avisos[":id"].leida.$post({ param: { id } }),
    invalidar: ["avisos"],
  });
  const evaluar = useAccion<void, { campanas: number; tareasRetrasadas: number; enRiesgo: string[]; avisos: number; correos: number }>({
    fn: (api) => api.api.campanas.riesgo.evaluar.$post(),
    invalidar: ["avisos", "bandeja", "calendario", "historial"],
    exito: (r) =>
      `${plural(r.campanas, "campaña revisada", "campañas revisadas")}: ${plural(r.tareasRetrasadas, "tarea retrasada", "tareas retrasadas")}, ${r.enRiesgo.length} en riesgo, ${plural(r.avisos, "aviso nuevo", "avisos nuevos")}${r.correos ? `, ${plural(r.correos, "correo", "correos")}` : ""}.`,
  });

  if (!conSesion) return <Vacio titulo="Entra para ver tus avisos" />;
  if (q.isLoading) return <EsqueletoTabla columnas={2} />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo cargar" texto={q.error?.message} />;
  const avisos = q.data.avisos;
  const sinLeer = avisos.filter((a) => !a.leidaEn).length;
  return (
    <>
      <TituloPagina
        descripcion={`Todo lo que el sistema mandó por correo, en un solo lugar. ${sinLeer === 0 ? "Nada sin leer." : `${sinLeer} sin leer.`}`}
        accion={
          esMkt ? (
            <Boton variante="contorno" disabled={evaluar.isPending} onClick={() => evaluar.mutate()}>
              {evaluar.isPending ? "Revisando…" : "Revisar riesgo ahora"}
            </Boton>
          ) : undefined
        }
      >
        Avisos
      </TituloPagina>
      {!q.data.correoConfigurado && (
        <Nota className="mb-5">
          El correo real (Resend) no está configurado en este entorno: los avisos se registran aquí y en el resumen diario, pero no salen por correo.
        </Nota>
      )}
      {esMkt && (
        <p className="mb-5 font-ui text-sm text-tinta-tenue">
          El cron corre todos los días a las 9:00 (Saltillo): marca tareas retrasadas, pone campañas en riesgo y manda recordatorios. «Revisar riesgo ahora» hace lo mismo en este momento.
        </p>
      )}
      {avisos.length === 0 ? (
        <Vacio titulo="Sin avisos" texto="Cuando una tarea venza, una campaña entre en riesgo o alguien pida una aprobación, aparece aquí." />
      ) : (
        <ul className="divide-y divide-linea border-y border-linea">
          {avisos.map((a) => (
            <li key={a.id} className="grid gap-2 py-3 sm:grid-cols-[1fr_auto] sm:items-start">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tono={a.leidaEn ? "neutro" : tonoDe(a.titulo)}>{categoria(a.titulo)}</Pill>
                  <p className={`font-ui text-sm ${a.leidaEn ? "text-tinta-tenue" : "font-semibold text-tinta"}`}>{a.titulo}</p>
                </div>
                {a.texto && <p className="mt-1 font-ui text-sm text-tinta-2">{a.texto}</p>}
                <p className="mt-1 font-ui text-xs text-tinta-tenue">
                  {fechaHora(a.creadoEn)}
                  {a.canal === "correo" ? (a.enviadaEn ? " · enviado por correo" : " · correo pendiente (sin Resend)") : ""}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                {a.url && (
                  <Boton variante="liga" tamano="sm" asChild>
                    <a href={a.url}>Abrir</a>
                  </Boton>
                )}
                {!a.leidaEn && (
                  <Boton variante="contorno" tamano="sm" onClick={() => leida.mutate(a.id)}>
                    Marcar leído
                  </Boton>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
